import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { CHAT_STRINGS_PT, ChatStrings } from '../../chat.strings';
import { ChatNextStep } from '../../chat.model';
import { ChatIconName } from '../chat-icon/chat-icon.component';

/**
 * CRM: a faixa "próximo passo" entre o fio e o compositor — uma linha e um
 * botão, calculada pelo contentor a partir do que já aconteceu na conversa
 * (qualificação respondida → enviar imóveis; imóveis enviados → propor
 * horários; visita passada → pedir avaliação; …). Só markup e @Outputs.
 */
@Component({
  selector: 'chat-next-step',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
<div *ngIf="step" class="chat-next" role="status">
  <span class="chat-next__icon"><chat-icon [name]="icon" [size]="15"></chat-icon></span>
  <span class="chat-next__text">{{ step.text }}</span>
  <button type="button" class="chat-next__btn" [disabled]="busy" (click)="act.emit(step)">
    <span *ngIf="busy" class="chat-next__spinner" aria-hidden="true"></span>{{ step.action }}
  </button>
  <button type="button" class="chat-next__x" [attr.aria-label]="strings.nextStepDismiss" [title]="strings.nextStepDismiss" (click)="dismiss.emit(step)">
    <chat-icon name="close" [size]="13"></chat-icon>
  </button>
</div>`,
  styles: [`
:host { display: block; flex-shrink: 0; }
.chat-next { display: flex; align-items: center; gap: 10px; margin: 0 16px 6px; padding: 8px 8px 8px 12px; border: 1px solid rgba(var(--chat-accent-rgb), .25); border-radius: 12px; background: rgba(var(--chat-accent-rgb), .07); color: var(--chat-ink); font-size: 12.5px; animation: chat-next-in .25s ease-out; }
.chat-next__icon { width: 26px; height: 26px; flex-shrink: 0; border-radius: 50%; background: rgba(var(--chat-accent-rgb), .15); color: var(--chat-accent); display: inline-flex; align-items: center; justify-content: center; }
.chat-next__text { flex-grow: 1; min-width: 0; }
.chat-next__btn { display: inline-flex; align-items: center; gap: 6px; height: 30px; padding: 0 12px; border: 0; border-radius: 8px; background: var(--chat-accent); color: var(--chat-on-accent); font: inherit; font-size: 12.5px; font-weight: 600; white-space: nowrap; cursor: pointer; }
.chat-next__btn[disabled] { opacity: .65; cursor: default; }
.chat-next__btn:hover:not([disabled]) { filter: brightness(1.08); }
.chat-next__x { width: 26px; height: 26px; flex-shrink: 0; border: 0; border-radius: 6px; background: transparent; color: var(--chat-muted); display: inline-flex; align-items: center; justify-content: center; cursor: pointer; }
.chat-next__x:hover { background: var(--chat-hover); color: var(--chat-ink); }
.chat-next__spinner { width: 12px; height: 12px; border: 2px solid rgba(255,255,255,.4); border-top-color: var(--chat-on-accent); border-radius: 50%; animation: chat-next-spin .8s linear infinite; }
@keyframes chat-next-in { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: none; } }
@keyframes chat-next-spin { to { transform: rotate(360deg); } }
@media (prefers-reduced-motion: reduce) { .chat-next, .chat-next__spinner { animation: none; } }
@media (max-width: 575.98px) { .chat-next { margin: 0 10px 6px; flex-wrap: wrap; } .chat-next__text { flex-basis: calc(100% - 36px); } }
`]
})
export class ChatNextStepComponent {
  @Input() step: ChatNextStep | null = null;
  @Input() busy = false;
  @Input() strings: ChatStrings = CHAT_STRINGS_PT;
  @Output() act = new EventEmitter<ChatNextStep>();
  @Output() dismiss = new EventEmitter<ChatNextStep>();

  get icon(): ChatIconName {
    switch (this.step && this.step.key) {
      case 'confirmVisit': return 'check';
      case 'sendProperties': return 'home';
      case 'qualify': return 'help';
      case 'proposeSlots': return 'calendar';
      case 'requestForm': return 'file';
      case 'proposal': return 'file';
      default: return 'bolt';
    }
  }
}
